import type {
  HealthcareService,
  Patient,
  Practitioner,
  Reference,
  ResourceType,
} from '@medplum/fhirtypes'
import type { SeenMedplumClient as MedplumClient } from '@seen/core/client'
import type { QueryClient } from '@tanstack/react-query'

const allAppointments = ['Appointment'] as const
export const readHistoryCacheKeyIdentifier = 'readHistory' as const
export const readVersionCacheKeyIdentifier = 'readVersion' as const

/**
 * Manages [Query keys](https://tanstack.com/query/latest/docs/react/guides/query-keys) and
 * cache optimizations / invalidations.
 */
export const cacheKeys = {
  read: (
    type: ResourceType | undefined,
    id: string,
    options?: Parameters<MedplumClient['readResource']>[2] | null
  ) => [type, id, 'read', options] as const,

  readExtended: (
    type: ResourceType | undefined,
    id: string,
    className: string,
    options?: Parameters<MedplumClient['readResource']>[2] | null
  ) => [type, id, 'readExtended', className, options] as const,

  readVersion: (
    type: ResourceType | undefined,
    id: string,
    versionId: string,
    className: string,
    options?: Parameters<MedplumClient['readResource']>[2] | null
    // key by readVersion first so versions don't get invalidated when the resource is updated (creates a new version)
  ) =>
    [
      type,
      readVersionCacheKeyIdentifier,
      id,
      versionId,
      className,
      options,
    ] as const,

  bulkReadVersions: (
    type: ResourceType | undefined,
    referencesKey: string,
    className: string
  ) => [type, 'bulkReadVersions', referencesKey, className] as const,

  bulkReadHistoryExtended: (
    type: ResourceType | undefined,
    ids: readonly string[],
    className: string
  ) =>
    [
      type,
      readHistoryCacheKeyIdentifier,
      'bulkReadHistoryExtended',
      className,
      ids,
    ] as const,

  readHistory: (
    type: ResourceType | undefined,
    id: string,
    options?: Parameters<MedplumClient['readResource']>[2] | null
  ) => [type, id, readHistoryCacheKeyIdentifier, options] as const,

  readHistoryExtended: (
    type: ResourceType | undefined,
    id: string,
    className: string,
    options?: Parameters<MedplumClient['readResource']>[2] | null
  ) =>
    [
      type,
      id,
      readHistoryCacheKeyIdentifier,
      'readHistoryExtended',
      className,
      options,
    ] as const,

  search: (type: ResourceType | undefined, searchKey?: string) =>
    [type, 'search', searchKey] as const,

  searchResourcePages: (type: ResourceType | undefined, searchKey?: string) =>
    [type, 'searchResourcePages', searchKey] as const,

  searchExtended: (type: ResourceType | undefined, searchKey?: string) =>
    [type, 'searchExtended', searchKey] as const,

  searchExtendedInfinite: (
    type: ResourceType | undefined,
    searchKey?: string
  ) => [type, 'searchExtendedInfinite', searchKey] as const,

  searchOne: (type: ResourceType | undefined, searchKey?: string) =>
    [type, 'searchOne', searchKey] as const,

  searchCount: (type: ResourceType | undefined, searchKey?: string) =>
    [type, 'searchCount', searchKey] as const,

  searchOneExtended: (type: ResourceType | undefined, searchKey?: string) =>
    [type, 'searchOneExtended', searchKey] as const,

  fullTextSearch: (type: ResourceType | undefined, searchKey?: string) =>
    [type, 'fullTextSearch', searchKey] as const,

  schema: (resourceType: ResourceType) => [resourceType, 'schema'] as const,
  elementBindingValueSet: (definition: string) =>
    ['elementBindingValueSet', definition] as const,

  invalidateMutation: (
    queryClient: QueryClient,
    type: ResourceType | undefined,
    id: string
  ) => {
    void queryClient.invalidateQueries({ queryKey: [type, id] })
    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === type &&
        query.queryKey[1] === readHistoryCacheKeyIdentifier &&
        query.queryKey[2] === 'bulkReadHistoryExtended' &&
        Array.isArray(query.queryKey[4]) &&
        query.queryKey[4].includes(id),
    })

    void queryClient.invalidateQueries({ queryKey: [type, 'search'] })
    void queryClient.invalidateQueries({ queryKey: [type, 'searchExtended'] })
    void queryClient.invalidateQueries({
      queryKey: [type, 'searchExtendedInfinite'],
    })
    // Paged searches must be swept too, or every `useSearchResourcePages`
    // consumer keeps serving a list that predates this write.
    void queryClient.invalidateQueries({
      queryKey: [type, 'searchResourcePages'],
    })
    void queryClient.invalidateQueries({ queryKey: [type, 'searchOne'] })
    void queryClient.invalidateQueries({ queryKey: [type, 'searchCount'] })
    void queryClient.invalidateQueries({
      queryKey: [type, 'searchOneExtended'],
    })
    void queryClient.invalidateQueries({ queryKey: [type, 'fullTextSearch'] })
  },

  // Invalidates every query for a resource type (reads, searches,
  // histories). Use after bulk mutations that touch many resources of one
  // type, where per-id invalidation would be noisier than a type-wide sweep.
  invalidateResourceType: (
    queryClient: QueryClient,
    type: ResourceType
  ): void => {
    void queryClient.invalidateQueries({ queryKey: [type] })
  },

  allAppointments,
  appointments: (searchKey: string) =>
    [...allAppointments, 'list', searchKey] as const,

  recommendedAppointmentSlots: ({
    patient,
    healthcareService,
    practitioner,
    start,
    end,
    excludeAppointmentId,
  }: {
    patient?: Reference<Patient>
    healthcareService?: Reference<HealthcareService>
    practitioner?: Reference<Practitioner>
    start?: string
    end?: string
    excludeAppointmentId?: string
  }) =>
    [
      ...allAppointments,
      'recommended-slots',
      patient,
      healthcareService,
      practitioner,
      start,
      end,
      excludeAppointmentId,
    ] as const,
  aiRecommendedAppointmentSlots: ({
    patient,
    healthcareService,
    practitioner,
    start,
    end,
    regenNonce,
    excludeAppointmentId,
  }: {
    patient?: Reference<Patient>
    healthcareService?: Reference<HealthcareService>
    practitioner?: Reference<Practitioner>
    start?: string
    end?: string
    // Bumped on each regenerate so a retry refetches even when nothing else in
    // the key changed.
    regenNonce?: number
    // The visit being moved. Distinct from a new booking of the same context,
    // which must not reuse a cache that still treats that visit as busy.
    excludeAppointmentId?: string
  }) =>
    [
      ...allAppointments,
      'ai-recommended-slots',
      patient,
      healthcareService,
      practitioner,
      start,
      end,
      regenNonce,
      excludeAppointmentId,
    ] as const,
  /* The Day Center note a clinician started for one participant-day, held
   * outside the `Observation` key space on purpose: `invalidateMutation` sweeps
   * every `[Observation, …]` search entry, and a day search that misses a
   * just-written note would then erase it and offer the start buttons again for
   * a day that is already charted. Nothing invalidates this entry, so it
   * outlives the row unmounting. */
  dayCenterDayNoteStarted: (
    participantReference: string | undefined,
    date: string
  ) => ['day-center-day-note-started', participantReference, date] as const,

  transportManifest: (
    date: string | undefined,
    patientId: string | undefined
  ) => [...allAppointments, 'transport-manifest', date, patientId] as const,
  rafScore: (patientId: string | undefined, includeBreakdown = false) =>
    ['seen-api', 'raf-score', patientId, includeBreakdown] as const,
  icd10RafWeight: (code: string) =>
    ['seen-api', 'icd10-raf-weight', code] as const, // gitleaks:allow — query key, not a credential
  icd10RafWeightsBatch: (codes: string[]) =>
    ['seen-api', 'icd10-raf-weights-batch', codes] as const,
}
